/**
 * Supplies terminology specific to the namespace / datasource.
 */
export interface ITerminologyService {
  /**
   * The datasource namespace display name (e.g. Invenias, Bullhorn, Salesforce, etc.)
   */
  get name(): string;

  /**
   * The indefinite article to use (i.e. 'a' or 'an') for the name (e.g. an Invenias, a Bullhorn, etc.)
   */
  get anarthrous(): string;

  /**
   * The record name (e.g. record, entity, etc.)
   */
  get recordName(): string;

  /**
   * The plural version of the record name (e.g. records, entities, etc.)
   */
  get recordNamePlural(): string;

  /**
   * The indefinite article to use (i.e. 'a' or 'an') for the record (e.g. a record, an entity, etc.)
   */
  get recordAnarthrous(): string;

  /**
   * The recipient type (e.g. Person, Entity)
   */
  get recipientType(): string;

   /**
   * Type of file (e.g. File, Document). Bullhorn and Salesforce use File, Invenias uses Document
   */
  get fileType(): string;
}

/**
 * Terminology placeholders to be used with TerminologySentenceBuilder.
 */
export class TermPlaceholder {
  /**
   * Placeholder for the namespace. Will be replaced by the terminology name (e.g. Invenias, Bullhorn)
   */
  public static readonly NAMESPACE = '<namespace>';

  /**
   * Placeholder for the record name. Will be replaced by the terminology record name (e.g. record, entity)
   */
  public static readonly RECORD_NAME = '<recordName>';

  /**
   * Placeholder for the plural record name. Will be replaced by the terminology plural record name (e.g. records, entities)
   */
  public static readonly RECORD_NAME_PLURAL = '<recordNamePlural>';

  /**
   * Placeholder for the anarthrous. Will be replaced by the terminology anarthrous (e.g. a, an)
   */
  public static readonly ANARTHROUS = '<an>';

  /**
   * Placeholder for the record anarthrous. Will be replaced by the terminology record anarthrous (e.g. a, an)
   */
  public static readonly RECORD_ANARTHROUS = '<recordAn>';

  /**
   * Placeholder for the recipient type. Will be replaced by the terminology recipient type (e.g. Person, Entity)
   */
  public static readonly RECIPIENT_TYPE = '<recipientType>';
}

/**
 * Helps build sentences using the namespace terminology.
 */
export class TerminologySentenceBuilder {
  private format = '';

  /**
   * Populate the sentence using the supplied terms.
   * @param sentence the formatted sentence containing terminology placeholders
   * @param terminology the service that provides the replacement terms
   * @returns the final sentence with the placeholders replaced by namespace specific terms
   */
  public static format(sentence: string, terminology: ITerminologyService): string {
    return sentence
      .replaceAll(TermPlaceholder.NAMESPACE, terminology.name)
      .replaceAll(TermPlaceholder.ANARTHROUS, terminology.anarthrous)
      .replaceAll(TermPlaceholder.RECORD_NAME, terminology.recordName)
      .replaceAll(TermPlaceholder.RECORD_NAME_PLURAL, terminology.recordNamePlural)
      .replaceAll(TermPlaceholder.RECORD_ANARTHROUS, terminology.recordAnarthrous)
      .replaceAll(TermPlaceholder.RECIPIENT_TYPE, terminology.recipientType);
  }

  public get name(): TerminologySentenceBuilder {
    return this.text(TermPlaceholder.NAMESPACE);
  }

  public get anarthrous(): TerminologySentenceBuilder {
    return this.text(TermPlaceholder.ANARTHROUS);
  }

  public get recordName(): TerminologySentenceBuilder {
    return this.text(TermPlaceholder.RECORD_NAME);
  }

  public get recordNamePlural(): TerminologySentenceBuilder {
    return this.text(TermPlaceholder.RECORD_NAME_PLURAL);
  }

  public get recordAnarthrous(): TerminologySentenceBuilder {
    return this.text(TermPlaceholder.RECORD_ANARTHROUS);
  }

  public get recipientType(): TerminologySentenceBuilder {
    return this.text(TermPlaceholder.RECIPIENT_TYPE);
  }

  public get space(): TerminologySentenceBuilder {
    return this.text(' ');
  }

  /**
   * Add a single space.
   */
  public get _(): TerminologySentenceBuilder {
    return this.text(' ');
  }

  /**
   * Add the literal text.
   */
  public text(text: string): TerminologySentenceBuilder {
    this.format += text;
    return this;
  }

  /**
   * Replace the placeholders in the sentence using the supplied terms.
   * @param terminologyService the service to use to get the terms
   * @returns the sentence with the terms replaced
   */
  public build(terminologyService: ITerminologyService): string {
    return TerminologySentenceBuilder.format(this.format, terminologyService);
  }
}
